import React from 'react';
import classNames from 'classnames';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import GoogleMapReact from 'google-map-react';
import supercluster from 'points-cluster';
import debounce from 'debounce';

import * as actionCreators from '../../actions/actionCreators';

import Marker from '../Marker/Marker';
import ClusterMarker from '../ClusterMarker/ClusterMarker';
import UserPosMarker from '../UserPosMarker/UserPosMarker';
import { MAX_ZOOM, MIN_ZOOM } from '../../constants/map';

import {
    fullscreenSupported,
    fullscreenActive,
    fullscreenEnter,
    fullscreenExit
} from '../../utils/fullScreen';

import { trackMapZoomChange } from '../../utils/analytics';

import { sortByDistance } from '../../utils/sortByDistance';

class Map extends React.Component {
    state = {
        clusters: [],
        mapProps: false,
        fullscreenActive: false
    };

    componentDidMount() {
        const ua = window.navigator.userAgent;
        const trident = ua.indexOf('Trident/');

        if (!fullscreenSupported() || trident > 0) {
            document.getElementById('fullScreenControl').style.display = 'none';
        }

        document.addEventListener(
            'fullscreenchange',
            this.toggleFullscreenState
        );
        document.addEventListener(
            'webkitfullscreenchange',
            this.toggleFullscreenState
        );
        document.addEventListener(
            'mozfullscreenchange',
            this.toggleFullscreenState
        );
        document.addEventListener(
            'msfullscreenchange',
            this.toggleFullscreenState
        );

        window.addEventListener('resize', this.handleWindowResize);

        const mapContainerDims = this.getMapContainerDimensions();
        this.props.setMapContainerDimensions(mapContainerDims);
    }

    // Notify parent about the markers currently in display
    componentDidUpdate() {
        if (!this.state.mapProps) {
            return;
        }

        const { setListMarkers, center, searchValue } = this.props;

        if (searchValue !== '') {
            const markersInSearchResult = this.props.items.filter((item) => {
                let words = searchValue.split(' ');
                words = words.map((word) => {
                    word = word.replace(/[!&/\\#,+()$~%.'":*?<>{}]/g, '');
                    return word.toLowerCase();
                });
                return words.every(
                    (word) =>
                        item.address1.toLowerCase().includes(word) ||
                        item.city.toLowerCase().includes(word) ||
                        item.machines.some((machine) =>
                            new RegExp(`^${word}`, 'g').test(machine.machine_id)
                        )
                );
            });
            setListMarkers(markersInSearchResult);
            return;
        }

        const filteredItems = this.filterItems(
            this.props.items,
            this.props.filter
        );

        let clusters = this.createClusters({
            zoom: this.state.mapProps.zoom,
            bounds: this.state.mapProps.bounds,
            items: filteredItems
        });

        let markersInbounds = [];
        clusters.forEach((cluster) => {
            cluster.points.forEach((point) => {
                markersInbounds.push(point.item);
            });
        });

        if (markersInbounds.length > 0) {
            markersInbounds = markersInbounds.map((item) => {
                item['y'] = item.point.coordinates[1];
                item['x'] = item.point.coordinates[0];
                return item;
            });
        }

        if (center.lat && center.lng) {
            const origin = { x: center.lng, y: center.lat };
            markersInbounds = sortByDistance(origin, markersInbounds);

            if (markersInbounds.length < 10) {
                let items = this.filterItems(
                    this.props.items,
                    this.props.filter
                );
                items = items.map((item) => {
                    const { coordinates = [] } = item.point || {};
                    const [x = null, y = null] = coordinates;

                    return {
                        ...item,
                        x,
                        y
                    };
                });
                markersInbounds = sortByDistance(origin, items).slice(0, 10);
            }
        }

        setListMarkers(markersInbounds);

        const mapContainerDims = this.getMapContainerDimensions();
        this.props.setMapContainerDimensions(mapContainerDims);
    }

    componentWillUnmount() {
        let doc = document;

        doc.removeEventListener('fullscreenchange', this.toggleFullscreenState);
        doc.removeEventListener(
            'webkitfullscreenchange',
            this.toggleFullscreenState
        );
        doc.removeEventListener(
            'mozfullscreenchange',
            this.toggleFullscreenState
        );
        doc.removeEventListener(
            'msfullscreenchange',
            this.toggleFullscreenState
        );

        window.removeEventListener('resize', this.handleWindowResize);
    }

    handleMapChange = ({ bounds, center, marginBounds, size, zoom }) => {
        trackMapZoomChange(this.state.mapProps.zoom, zoom);

        this.setState({
            mapProps: { bounds, center, marginBounds, size, zoom }
        });

        this.props.setCenter(center);
        this.props.setZoom(zoom);
    };

    handleMapApiLoaded = ({ maps, map }) => {
        this.setState({ apiLoaded: true }, () => {
            this.maps = maps;
            this.map = map;

            this.props.setMap(map);
            this.props.setInfoWindow(
                new this.maps.InfoWindow({
                    pixelOffset: new this.maps.Size(0, -20)
                })
            );
            this.map.controls[this.maps.ControlPosition.RIGHT_BOTTOM].push(
                document.getElementById('controls')
            );
            this.map.controls[this.maps.ControlPosition.TOP_RIGHT].push(
                document.getElementById('filters')
            );
        });
    };

    handleZoomIn = () => {
        const zoom = Math.min(this.props.zoom + 1, MAX_ZOOM);
        this.props.setCenter(this.props.center);
        this.props.setZoom(zoom);
    };

    handleZoomOut = () => {
        const zoom = Math.max(this.props.zoom - 1, MIN_ZOOM);
        this.props.setCenter(this.props.center);
        this.props.setZoom(zoom);
    };

    handleWindowResize = debounce(() => {
        const mapContainerDims = this.getMapContainerDimensions();
        this.props.setMapContainerDimensions(mapContainerDims);
    }, 300);

    fullscreenToggle = () => {
        if (fullscreenActive()) {
            fullscreenExit();
        } else {
            const map = document.querySelector('.gm-style');
            fullscreenEnter(map);
        }
    };

    toggleFullscreenState = () => {
        this.setState((prevState) => ({
            fullscreenActive: !prevState.fullscreenActive
        }));
    };

    getFormattedServices(language) {
        return [
            {
                value: '',
                label: this.props.labels.showAll,
                className: 'Select-option--all'
            },
            ...this.props.services.map((item) => {
                return {
                    value: item.value,
                    label: item[`name_${language}`],
                    className: `Select-option--${item.identifier}`,
                    link: item.link
                };
            })
        ];
    }

    handleMarkerClick = (id) => {
        const marker = this.props.items.find((item) => item.identifier === id);
        if (!marker) {
            return;
        }
        this.props.setCenter({ lat: marker.y, lng: marker.x });
        this.props.onMarkerClick(marker);
    };

    createClusters = ({ bounds, zoom, items }) => {
        if (!bounds) {
            return;
        }

        const index = supercluster(
            items.map((item) => {
                const { coordinates = [] } = item.point || {};
                const [lng = null, lat = null] = coordinates;

                return {
                    lat,
                    lng,
                    item
                };
            })
        );

        return index({
            bounds: {
                nw: { lat: bounds.nw.lat, lng: bounds.nw.lng },
                se: { lat: bounds.se.lat, lng: bounds.se.lng }
            },
            zoom: zoom
        });
    };

    filterItems = (items, filters) => {
        const filterDict = {};
        let anyActiveFilters = false; // Flag to check if any filters other than 'deposit' are active

        filters.forEach((filter) => {
            const currency = filter.value.toLowerCase(); // normalize currency to lowercase
            const activeDenominations = new Set();

            filter.options.forEach((option) => {
                if (option.active) {
                    activeDenominations.add(parseInt(option.value));
                    anyActiveFilters = true;
                }
            });

            if (activeDenominations.size > 0) {
                filterDict[currency] = activeDenominations;
            }

            if (currency === 'deposit' && activeDenominations.size > 0) {
                filterDict.deposit = true;
            }
        });

        if (!anyActiveFilters) {
            return items;
        }

        if (Object.keys(filterDict).length === 1 && filterDict.deposit) {
            return items.filter((item) =>
                item.services.some(
                    (service) =>
                        service.identifier === 'deposit' && service.available
                )
            );
        }

        return items.filter((item) => {
            const currencyMatch = Object.keys(filterDict).some((key) => {
                if (key !== 'deposit') {
                    const denominations = new Set(
                        item.currencies.find(
                            (currencyInfo) =>
                                currencyInfo.currency.toLowerCase() === key
                        )?.denomination || []
                    );
                    return (
                        filterDict[key] &&
                        [...denominations].some((den) =>
                            filterDict[key].has(den)
                        )
                    );
                }
                return false;
            });

            const depositServiceAvailable = filterDict.deposit
                ? item.services.some(
                      (service) =>
                          service.identifier === 'deposit' && service.available
                  )
                : true;

            return currencyMatch && depositServiceAvailable;
        });
    };

    // filterItems = (items, filter) => {
    //     console.log(items);
    //     console.log(filter);
    //     // TODO: fix with new filter
    //     return items;
    //     if (!Array.isArray(items) || items.length < 1) {return [];}
    //
    //     if (!filter) {
    //         return items.filter(i => Array.isArray(i.point.coordinates));
    //     }
    //
    //     let filtered = items.filter(item => {
    //         let groups = item.groups
    //             ? item.groups
    //             : [];
    //
    //         const identifiers = [
    //             ...item.services.map(service => service.identifier),
    //             ...groups
    //         ];
    //
    //         return identifiers.includes(filter) && Array.isArray(item.point.coordinates);
    //     });
    //
    //     return filtered;
    // };

    getMapContainerDimensions = () => {
        const mapRef = this.refs.map;
        const mapWidth = mapRef.clientWidth;
        const mapHeight = mapRef.clientHeight;

        const mapContainerDims = {
            width: mapWidth,
            height: fullscreenActive() ? screen.height : mapHeight
        };

        return mapContainerDims;
    };

    render() {
        const {
            items,
            center,
            googleMapKey,
            showFilters,
            labels,
            zoom,
            language,
            userPos,
            fetchedMachines,
            fetchedUserPosition
        } = this.props;

        const { fullscreenActive, mapProps } = this.state;

        // const options = this.getFormattedServices(language);
        const mapClasses = classNames('Map', {
            [`Map--Active`]:
                items.length > 0 && fetchedMachines && fetchedUserPosition
        });
        const loaderClasses = classNames('Map__Loader', {
            [`Map__Loader--Active`]: !fetchedMachines || !fetchedUserPosition
        });

        const filteredItems = this.filterItems(
            this.props.items,
            this.props.filter
        );

        let clusters = this.createClusters({
            zoom: mapProps.zoom,
            bounds: mapProps.bounds,
            items: filteredItems
        });

        clusters = clusters || [];

        return (
            <div className={mapClasses} ref="map">
                <div className={loaderClasses}>
                    <div className="loader" />
                </div>
                <div
                    id="controls"
                    className={classNames('Map__Controls', {
                        ['Map__Controls--hide']: showFilters
                    })}
                >
                    <button
                        id="fullScreenControl"
                        className={classNames('Map__FullscreenControl', {
                            ['Map__FullscreenControl--Active']: fullscreenActive
                        })}
                        onClick={this.fullscreenToggle}
                    >
                        {labels.toggleFullscreen}
                    </button>
                    <button
                        className="Map__ZoomControl Map__ZoomControl--In"
                        onClick={this.handleZoomIn}
                    >
                        {labels.zoomIn}
                    </button>
                    <button
                        className="Map__ZoomControl Map__ZoomControl--Out"
                        onClick={this.handleZoomOut}
                    >
                        {labels.zoomOut}
                    </button>
                </div>
                <GoogleMapReact
                    center={center}
                    zoom={zoom}
                    bootstrapURLKeys={{ key: googleMapKey }}
                    onChange={this.handleMapChange}
                    options={MAP_OPTIONS}
                    resetBoundsOnResize={true}
                    onGoogleApiLoaded={this.handleMapApiLoaded}
                    yesIWantToUseGoogleMapApiInternals={true}
                >
                    {userPos && (
                        <UserPosMarker lat={userPos.lat} lng={userPos.lng} />
                    )}
                    {clusters.map((item, index) =>
                        item.numPoints === 1 ? (
                            <Marker
                                key={index}
                                lat={item.wy}
                                lng={item.wx}
                                item={item.points[0].item}
                                labels={labels}
                                type="marker"
                                language={language}
                                onClick={this.handleMarkerClick}
                            />
                        ) : (
                            <ClusterMarker
                                key={index}
                                lat={item.wy}
                                lng={item.wx}
                                item={item}
                                type="clustermarker"
                            />
                        )
                    )}
                </GoogleMapReact>
            </div>
        );
    }
}

Map.defaultProps = {
    googleMapKey: '',
    items: [],
    labels: {},
    services: []
};

const MAP_OPTIONS = {
    scrollwheel: true,
    zoomControl: false,
    disableDefaultUI: true,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    rotateControl: false,
    clickableIcons: false,
    styles: [
        {
            featureType: 'water',
            stylers: [{ visibility: 'simplified' }, { color: '#B6DFE1' }]
        },
        {
            featureType: 'road',
            stylers: [{ saturation: -100 }, { gamma: 0.7 }]
        },
        {
            featureType: 'poi.park',
            stylers: [{ saturation: -100 }, { gamma: 2.6 }]
        },
        {
            featureType: 'landscape',
            stylers: [
                { saturation: -50 },
                { gamma: 1.2 },
                { visibility: 'simplified' }
            ]
        },
        {
            featureType: 'poi'
        },
        {
            featureType: 'transit'
        }
    ]
};

function mapStateToProps(state) {
    return {
        items: state.map.items || [],
        center: state.map.center,
        zoom: state.map.zoom,
        searchValueClicked: state.map.searchValueClicked,
        userPos: state.map.userPos,
        fetchedUserPosition: state.map.fetchedUserPosition,
        fetchedMachines: state.map.fetchedMachines,
        infowindow: state.map.infowindow,
        activeMarker: state.map.activeMarker,
        filter: state.map.filter,
        searchValue: state.map.searchValue
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(actionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Map);